// The telemetry facade (P0.11).
//
// Everything above this file calls `record()` and knows nothing else. Below it, three gates decide
// whether an SDK is ever downloaded — and the order matters, because each one is a different
// reason to send nothing:
//
//   1. **Consent.** Opted out → never fetched. FR-44's off switch, read as strongly as it can be.
//   2. **Configuration.** No project key → nothing to send to. Today this is every environment.
//   3. **Failure.** The import rejects, the network is gone, an ad blocker ate it → the app does
//      not care. Telemetry that can break the product is worse than no telemetry.
//
// The import is dynamic, and that is the whole design. `posthog-js` is 75 kB gzipped — heavier
// than React and ReactDOM together — and `@sentry/browser` another 29 kB. Static imports would put
// 101 kB in front of first interactive, which NFR-P4 measures, for code that contributes nothing
// to it. Stack §7's payload accounting has no telemetry line at all; P0.10's budget noticed.

import { hasConsent } from './consent';
import type { TelemetryEvent } from './events';
import { isConfigured, readConfig, type TelemetryTransport } from './transport';

/**
 * Events recorded before the transport finished loading.
 *
 * `app_booted` is recorded during boot and the transport arrives a moment later, so without a
 * queue the one event P0.11 ships would be the one event that never sends. Bounded, because an
 * unbounded queue behind a transport that never loads is a memory leak with a nice name.
 */
const PENDING_LIMIT = 32;
const pending: TelemetryEvent[] = [];

let transport: TelemetryTransport | null = null;
let loading: Promise<TelemetryTransport | null> | null = null;
let disabled = false;

/**
 * Loads the transport, once, if all three gates allow it.
 *
 * Returns null rather than throwing. Every caller is on a path where the right response to
 * "telemetry is broken" is to carry on: the user is trying to play music.
 */
async function loadTransport(): Promise<TelemetryTransport | null> {
  if (disabled) {
    return null;
  }
  if (transport !== null) {
    return transport;
  }

  const config = readConfig();
  if (!hasConsent() || !isConfigured(config)) {
    // Not an error and not temporary. Latch it off so every later event is a boolean check
    // rather than a repeated storage read and a repeated decision.
    disabled = true;
    pending.length = 0;
    return null;
  }

  loading ??= import('./posthog-transport')
    .then((module) => module.createPostHogTransport(config))
    .catch((cause: unknown) => {
      // An ad blocker, an offline user, a CSP that does not allow the host. All of these are the
      // world working as intended from someone's point of view, and none of them is the app's
      // problem. Log once, at debug, and stop trying.
      console.debug('[synapse] telemetry unavailable; continuing without it', cause);
      disabled = true;
      pending.length = 0;
      return null;
    });

  transport = await loading;
  return transport;
}

/**
 * Record an event. Never throws, never blocks, never makes the caller wait.
 *
 * Deliberately not async: an `await` here would put telemetry on the critical path of whatever
 * called it, and there is no version of this product where that is the right trade.
 */
export function record(event: TelemetryEvent): void {
  if (disabled) {
    return;
  }

  if (transport !== null) {
    transport.send(event);
    return;
  }

  if (pending.length < PENDING_LIMIT) {
    pending.push(event);
  }

  void loadTransport().then((loaded) => {
    if (loaded === null) {
      return;
    }
    for (const queued of pending.splice(0)) {
      loaded.send(queued);
    }
  });
}

/** Report an error. Same three gates, same silence when they say no. */
export function recordError(error: unknown): void {
  void loadTransport().then((loaded) => {
    loaded?.captureError(error);
  });
}

/**
 * Stop, immediately and permanently, for this page.
 *
 * Called when the user opts out mid-session. If the SDK already loaded, it stays loaded — it
 * cannot be un-downloaded — but nothing further is sent, and the next page load will not fetch it
 * at all. That asymmetry is worth knowing rather than glossing: the strong guarantee is about the
 * *next* session; this one degrades to silence.
 */
export function disable(): void {
  disabled = true;
  pending.length = 0;
  transport = null;
}

/** Test seam. Not exported from the barrel — nothing in the app should reach for it. */
export function __resetForTests(): void {
  transport = null;
  loading = null;
  disabled = false;
  pending.length = 0;
}
